
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import {
  RCS_AGENT_PURPOSE_DEFAULT,
  RCS_BILLING_CATEGORY_DEFAULT,
  RCS_HOSTING_REGION_DEFAULT,
  RCS_HOSTING_REGIONS,
} from '@/types/rcs-onboarding';
import type { RcsBrand, RcsDedicatedFormState } from '@/types/rcs-onboarding';

const SENDER_ID_REGEX = /^[A-Za-z0-9_-]{2,50}$/;
/** Sentinel dropdown value meaning "create a brand new brand". */
const NEW_BRAND_VALUE = '__new_brand__';
const buildSenderIdFromAgentName = (value: string): string => value
  .toLowerCase()
  // Convert any non-alphanumeric character to underscore.
  .replace(/[^a-z0-9]/g, '_')
  // Collapse repeating underscores.
  .replace(/_+/g, '_')
  // Remove underscores at both ends.
  .replace(/^_+|_+$/g, '')
  .slice(0, 50);

export default defineComponent({
  name: 'RcsBasicInfoStep',

  components: { InputText, Dropdown },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
    reusableBrands: {
      type: Array as PropType<RcsBrand[]>,
      default: () => [],
    },
  },

  setup(props) {
    const { t } = useI18n();

    const hasReusableBrands = computed(() => props.reusableBrands.length > 0);
    // Pre-select the agent's existing brand when it matches a reusable one,
    // otherwise default to creating a new brand.
    const brandSelection = ref<string>(
      props.form.basic_info.brand_id
        && props.reusableBrands.some((b) => b.id === props.form.basic_info.brand_id)
        ? props.form.basic_info.brand_id
        : NEW_BRAND_VALUE,
    );
    const isCreatingNewBrand = computed(
      () => !hasReusableBrands.value || brandSelection.value === NEW_BRAND_VALUE,
    );
    const brandOptions = computed(() => [
      ...props.reusableBrands.map((b) => ({ label: b.display_name, value: b.id })),
      {
        label: t('storeParameters.services.rcsServiceParameter.wizard.basicInfo.createNewBrand'),
        value: NEW_BRAND_VALUE,
      },
    ]);

    watch(brandSelection, (value) => {
      if (value === NEW_BRAND_VALUE) {
        props.form.basic_info.brand_id = '';
        props.form.basic_info.brand_display_name = '';
        return;
      }
      const brand = props.reusableBrands.find((b) => b.id === value);
      props.form.basic_info.brand_id = value;
      props.form.basic_info.brand_display_name = brand?.display_name || '';
    });

    props.form.basic_info.agent_purpose = RCS_AGENT_PURPOSE_DEFAULT;
    props.form.basic_info.billing_category = RCS_BILLING_CATEGORY_DEFAULT;
    if (!props.form.basic_info.hosting_region) {
      props.form.basic_info.hosting_region = RCS_HOSTING_REGION_DEFAULT;
    }

    const hostingRegionOptions = computed(() => RCS_HOSTING_REGIONS.map((value) => ({
      value,
      label: t(`storeParameters.services.rcsServiceParameter.wizard.basicInfo.hostingRegions.${value}`),
    })));

    const generatedFromCurrentName = buildSenderIdFromAgentName(
      props.form.basic_info.agent_display_name || '',
    );
    // If a custom sender id is already present, do not override it.
    const senderIdManuallyEdited = ref(
      !!props.form.basic_info.sender_id
      && props.form.basic_info.sender_id !== generatedFromCurrentName,
    );

    watch(
      () => props.form.basic_info.agent_display_name,
      (value) => {
        if (senderIdManuallyEdited.value) return;
        props.form.basic_info.sender_id = buildSenderIdFromAgentName(value || '');
      },
      { immediate: true },
    );

    const senderIdError = computed(() => {
      const value = props.form.basic_info.sender_id;
      if (!value) return null;
      if (!SENDER_ID_REGEX.test(value)) return 'invalidSenderId';
      return null;
    });

    const handleSenderIdManualInput = () => {
      senderIdManuallyEdited.value = true;
    };

    return {
      t,
      hostingRegionOptions,
      senderIdError,
      handleSenderIdManualInput,
      hasReusableBrands,
      isCreatingNewBrand,
      brandSelection,
      brandOptions,
    };
  },
});
